import type { SegmentDefinition } from "../../types/planner";

/**
 * §1.6 — starting points for Tab 1. Every row in `product_facts` already
 * clears the 3-merchant floor, so no preset restates it, and only "High
 * spread" and "Deep comparison value" touch `spread_pct` — it is never a
 * default.
 */
export interface Preset {
	id: string;
	label: string;
	description: string;
	definition: SegmentDefinition;
}

export const PLANNER_PRESETS: Preset[] = [
	{
		id: "everything",
		label: "Everything",
		description: "No filters — the raw nightly set.",
		definition: { match: "all", filters: [] },
	},
	{
		id: "ad-ready",
		label: "Ad-ready",
		description:
			"Has image + description, not policy-blocked, approved by CSS.",
		definition: {
			match: "all",
			filters: [
				{ field: "has_image", op: "is", value: true },
				{ field: "has_description", op: "is", value: true },
				{ field: "css_policy_blocked", op: "is", value: false },
				{ field: "css_serving_status", op: "in", value: ["approved"] },
			],
		},
	},
	{
		id: "needs-content",
		label: "Needs content",
		description:
			"No generated content yet — the content backlog, richest traffic first.",
		definition: {
			match: "all",
			filters: [{ field: "generated_content", op: "is", value: false }],
			sort: { field: "gsc_impressions_30d", dir: "desc" },
		},
	},
	{
		id: "well-rated",
		label: "Well rated",
		description: "Rating 4+ with at least 5 reviews.",
		definition: {
			match: "all",
			filters: [
				{ field: "review_rating", op: "gte", value: 4 },
				{ field: "review_count", op: "gte", value: 5 },
			],
		},
	},
	{
		id: "high-spread",
		label: "High spread",
		description: "Price spread of 15% or more.",
		definition: {
			match: "all",
			filters: [{ field: "spread_pct", op: "gte", value: 15 }],
		},
	},
	{
		id: "deep-comparison-value",
		label: "Deep comparison value",
		description:
			"6+ merchants and 10%+ spread — the strongest comparison candidates.",
		definition: {
			match: "all",
			filters: [
				{ field: "merchant_count", op: "gte", value: 6 },
				{ field: "spread_pct", op: "gte", value: 10 },
			],
		},
	},
	{
		id: "striking-distance",
		label: "Striking distance",
		description:
			"Average position 5–15 with 50+ impressions in the last 30 days.",
		definition: {
			match: "all",
			filters: [
				{ field: "gsc_avg_position_30d", op: "between", value: [5, 15] },
				{ field: "gsc_impressions_30d", op: "gte", value: 50 },
			],
		},
	},
	{
		id: "fresh",
		label: "Fresh",
		description: "Products created in the last 30 days.",
		definition: {
			match: "all",
			filters: [{ field: "product_created_at", op: "within_days", value: 30 }],
		},
	},
	{
		id: "price-droppers",
		label: "Price droppers",
		description: "Price down 10%+ over the last 4 weeks.",
		definition: {
			match: "all",
			filters: [{ field: "price_change_pct", op: "lte", value: -10 }],
		},
	},
];
